import React from 'react';
import PropTypes from 'prop-types';
import { useDeepCompareMemo } from 'use-deep-compare';

import { ref as PropTypesRef } from 'utilsTS/prop-types';
import { MatchMediaContext } from 'containers/MatchMediaProvider';
import { TIER } from './constants';
import { ContainerQueryContext } from './ContainerQueryContext';
import { useContainerQuery } from './useContainerQuery';

export type ContainerQueryRenderProp = (
    options: ReturnType<typeof useContainerQuery>,
) => React.ReactNode;

export interface ContainerQueryProviderProps {
    /** Defines the container to use */
    containerRef?: React.Ref<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
    /** Defines the tier to fall back to */
    defaultTier?: TIER;
    /** Defines a style object exporting container widths */
    widths?: Record<string, string>;
    /** Defines the prefix of the exported container width names */
    widthPrefix?: string;
    /** Defines content of provider */
    children?: React.ReactNode | ContainerQueryRenderProp;
}

export const ContainerQueryProvider: React.FC<ContainerQueryProviderProps> = ({
    containerRef,
    defaultTier,
    widths,
    widthPrefix = '',
    children,
}) => {
    const { matchingTier: matchMediaTier } = React.useContext(MatchMediaContext);
    const containerQueryProps = useContainerQuery(widths, widthPrefix, { ref: containerRef });
    const { width, tierWidths, matchingTier, matchingTiers, isMatchingTier, getTierProp } =
        containerQueryProps;

    const context = useDeepCompareMemo(
        () => ({
            width,
            matchingTier,
            matchingTiers,
            isMatchingTier,
            getTierProp,
            defaultTier: defaultTier || matchMediaTier,
            tierWidths,
        }),
        [width, matchingTier, matchingTiers, isMatchingTier, defaultTier, tierWidths],
    );

    const content = typeof children === 'function' ? children(containerQueryProps) : children;

    return (
        <ContainerQueryContext.Provider value={context}>{content}</ContainerQueryContext.Provider>
    );
};

ContainerQueryProvider.displayName = 'ContainerQueryProvider';
ContainerQueryProvider.propTypes = {
    containerRef: PropTypesRef(),
    defaultTier: PropTypes.oneOf(Object.values(TIER)),
    widths: PropTypes.shape({}),
    widthPrefix: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};
